import React from 'react';
// Deep import, not the `../../ui` barrel — see the import comment in
// PmlSnapshotPage.jsx for why (the barrel eagerly requires mapbox-gl, which
// throws in jsdom). hexMapStyles.js is plain JS with no React/mapbox deps.
import { formatUsd, STATUS_COLORS } from './hexMapStyles';

const isNil = (v) => v === null || v === undefined;

const VERDICT_COLOR = { OK: STATUS_COLORS.green, WATCH: STATUS_COLORS.amber, ACTION: STATUS_COLORS.red };
const VERDICT_FALLBACK_COLOR = '#8A94A6';

// `exec.reinsurance.worst_occ_pml_250` — the largest single-occurrence 250-yr PML
// across this month's cells — is the cession-headroom numerator per the artifact
// contract (2026-07-22-artifact-contract.md). It's only just landed there, so a
// summary payload that predates the backend change may omit or null it; in that
// case the page computes `fallbackWorstOcc` (max pml250Occ across the concerns +
// top10 arrays it already has in hand) and passes it down. Either way this stays
// null-tolerant: missing/undefined renders as '—' rather than crashing or showing
// a nonsense percentage.
const pct = (numerator, denominator) => {
  if (isNil(numerator) || isNil(denominator) || Number(denominator) === 0) return null;
  return (Number(numerator) / Number(denominator)) * 100;
};

const formatPct = (v) => (isNil(v) ? '—' : `${Math.round(v)}%`);
const formatCount = (v) => (isNil(v) ? '—' : Number(v).toLocaleString('en-US'));

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 12 },
  headline: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  chip: { display: 'inline-block', padding: '2px 10px', borderRadius: 12, color: '#fff', fontWeight: 700, fontSize: 13, letterSpacing: '0.02em' },
  note: { color: '#454F5B' },
  tiles: { display: 'flex', flexDirection: 'row', gap: 32, flexWrap: 'wrap' },
  tile: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 160 },
  tileLabel: { fontSize: 12, color: '#637381', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.02em' },
  tileValue: { fontSize: 20, fontWeight: 700, color: '#212B36' },
  tileSub: { fontSize: 12, color: '#919EAB' },
};

// Pure presentation: verdict chip, verdict note, and three stat tiles (cession
// headroom, red/amber counts, rooftop reconciliation). Every field is optional —
// `exec` may be null/{} while the summary is loading or the API omits a section.
// `fallbackWorstOcc` is optional too: the page only needs to compute and pass it
// when `exec.reinsurance.worst_occ_pml_250` isn't populated yet.
const SnapshotStats = ({ exec, listing, fallbackWorstOcc }) => {
  const verdict = exec?.verdict;
  const verdictColor = VERDICT_COLOR[verdict] || VERDICT_FALLBACK_COLOR;

  const worstOccFromExec = exec?.reinsurance?.worst_occ_pml_250;
  const worstOcc = isNil(worstOccFromExec) ? fallbackWorstOcc : worstOccFromExec;
  const cessionCap = exec?.reinsurance?.occ_cession_cap;
  const headroomPct = pct(worstOcc, cessionCap);

  const redCount = listing?.redCount ?? 0;
  const amberCount = listing?.amberCount ?? 0;

  const modeled = exec?.reconciliation?.modeled_rooftops;
  const declared = exec?.reconciliation?.declared_rooftops;
  const dropped = exec?.reconciliation?.dropped_non_modelable;
  const declaredTiv = exec?.reconciliation?.declared_tiv;

  return (
    <div data-testid="snapshot-stats" style={S.wrap}>
      <div style={S.headline}>
        {verdict && <span style={{ ...S.chip, backgroundColor: verdictColor }}>{verdict}</span>}
        {exec?.verdict_note && <span style={S.note}>{exec.verdict_note}</span>}
      </div>
      <div style={S.tiles}>
        <div style={S.tile}>
          <span style={S.tileLabel}>Cession headroom</span>
          <span style={S.tileValue}>{formatPct(headroomPct)}</span>
          <span style={S.tileSub}>{`${formatUsd(worstOcc)} of ${formatUsd(cessionCap)} occ cap`}</span>
        </div>
        <div style={S.tile}>
          <span style={S.tileLabel}>Red / amber cells</span>
          <span style={S.tileValue}>{`${redCount} / ${amberCount}`}</span>
        </div>
        <div style={S.tile}>
          <span style={S.tileLabel}>Rooftop reconciliation</span>
          <span style={S.tileValue}>{`${formatCount(modeled)} / ${formatCount(declared)}`}</span>
          <span style={S.tileSub}>{`${formatCount(dropped)} dropped`}</span>
          <span style={S.tileSub}>{`${formatUsd(declaredTiv)} declared TIV`}</span>
        </div>
      </div>
    </div>
  );
};

export default SnapshotStats;
